import { create } from 'zustand';
import apiClient from '../api/api';
import toast from 'react-hot-toast';

const useServiceOrderStore = create((set, get) => ({
  orders: [],
  myOrders: [],
  selectedOrder: null,
  isLoading: false,
  error: null,

  // Crear pedido de servicio
  createOrder: async (orderData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post('/service-orders', orderData);
      
      if (response.data.success) {
        const newOrder = response.data.order;
        set((state) => ({
          orders: [newOrder, ...state.orders],
          myOrders: [newOrder, ...state.myOrders],
          isLoading: false,
        }));
        toast.success('Pedido creado exitosamente');
        return { success: true, order: newOrder };
      }
      
      set({ isLoading: false });
      return { success: false };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al crear pedido';
      set({ isLoading: false, error: errorMsg });
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  },

  // Obtener mis pedidos
  fetchMyOrders: async (role = null, status = null) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (role) params.append('role', role);
      if (status) params.append('status', status);

      const response = await apiClient.get(`/service-orders/my-orders?${params}`);
      
      if (response.data.success) {
        set({
          myOrders: response.data.orders || [],
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Error al cargar pedidos',
      });
    }
  },

  // Obtener pedido específico
  fetchOrderById: async (orderId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get(`/service-orders/${orderId}`);
      
      if (response.data.success) {
        set({
          selectedOrder: response.data.order,
          isLoading: false,
        });
        return response.data.order;
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Error al cargar pedido',
      });
      return null;
    }
  },

  // Aceptar pedido (player)
  acceptOrder: async (orderId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post(`/service-orders/${orderId}/accept`);
      
      if (response.data.success) {
        const updatedOrder = response.data.order;
        set((state) => ({
          myOrders: state.myOrders.map((o) =>
            o._id === orderId ? updatedOrder : o
          ),
          selectedOrder: state.selectedOrder?._id === orderId ? updatedOrder : state.selectedOrder,
          isLoading: false,
        }));
        toast.success('Pedido aceptado');
        return { success: true, order: updatedOrder };
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al aceptar pedido';
      set({ isLoading: false, error: errorMsg });
      toast.error(errorMsg);
      return { success: false };
    }
  },

  // Completar pedido (player)
  completeOrder: async (orderId, deliverables = []) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post(`/service-orders/${orderId}/complete`, {
        deliverables,
      });
      
      if (response.data.success) {
        const updatedOrder = response.data.order;
        set((state) => ({
          myOrders: state.myOrders.map((o) =>
            o._id === orderId ? updatedOrder : o
          ),
          selectedOrder: state.selectedOrder?._id === orderId ? updatedOrder : state.selectedOrder,
          isLoading: false,
        }));
        toast.success('Pedido marcado como completado');
        return { success: true, order: updatedOrder };
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al completar pedido';
      set({ isLoading: false, error: errorMsg });
      toast.error(errorMsg);
      return { success: false };
    }
  },

  // Confirmar entrega (cliente)
  confirmDelivery: async (orderId, rating = null, comment = '') => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post(`/service-orders/${orderId}/confirm-delivery`, {
        rating,
        comment,
      });
      
      if (response.data.success) {
        const updatedOrder = response.data.order;
        set((state) => ({
          myOrders: state.myOrders.map((o) =>
            o._id === orderId ? updatedOrder : o
          ),
          selectedOrder: state.selectedOrder?._id === orderId ? updatedOrder : state.selectedOrder,
          isLoading: false,
        }));
        toast.success('Entrega confirmada');
        return { success: true, order: updatedOrder };
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al confirmar entrega';
      set({ isLoading: false, error: errorMsg });
      toast.error(errorMsg);
      return { success: false };
    }
  },

  // Cancelar pedido
  cancelOrder: async (orderId, reason = '') => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post(`/service-orders/${orderId}/cancel`, {
        reason,
      });
      
      if (response.data.success) {
        const updatedOrder = response.data.order;
        set((state) => ({
          myOrders: state.myOrders.map((o) =>
            o._id === orderId ? updatedOrder : o
          ),
          selectedOrder: state.selectedOrder?._id === orderId ? updatedOrder : state.selectedOrder,
          isLoading: false,
        }));
        toast.success('Pedido cancelado');
        return { success: true, order: updatedOrder };
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al cancelar pedido';
      set({ isLoading: false, error: errorMsg });
      toast.error(errorMsg);
      return { success: false };
    }
  },

  // Añadir nota al pedido
  addNote: async (orderId, message) => {
    try {
      const response = await apiClient.post(`/service-orders/${orderId}/notes`, {
        message,
      });
      
      if (response.data.success) {
        const newNote = response.data.note;
        set((state) => ({
          selectedOrder: state.selectedOrder?._id === orderId
            ? { ...state.selectedOrder, notes: [...(state.selectedOrder.notes || []), newNote] }
            : state.selectedOrder,
        }));
        return { success: true, note: newNote };
      }
    } catch (error) {
      toast.error('Error al añadir nota');
      return { success: false };
    }
  },

  // Actualizar pedido desde socket
  updateOrderFromSocket: (orderId, updates) => {
    set((state) => ({
      myOrders: state.myOrders.map((o) =>
        o._id === orderId ? { ...o, ...updates } : o
      ),
      selectedOrder: state.selectedOrder?._id === orderId
        ? { ...state.selectedOrder, ...updates }
        : state.selectedOrder,
    }));
  },

  // Limpiar orden seleccionada
  clearSelectedOrder: () => set({ selectedOrder: null }),

  // Reset store
  reset: () => set({
    orders: [],
    myOrders: [],
    selectedOrder: null,
    isLoading: false,
    error: null,
  }),
}));

export default useServiceOrderStore;
